'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {zodResolver} from '@hookform/resolvers/zod';
import {useParams} from 'next/navigation';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {FileUpload, FormError, TextArea, TextField} from '@/components';
import {isRequired, maxLength} from '@/lib/utils/common';
import {
  getRootError,
  getSchemaShape,
  getSfdcLanguage,
  handleError,
  onSubmitError,
  tSafe,
} from '../../utils';
import {Consent} from '../Consent/Consent';
import {Description, FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';

function FiberSocialForm(): React.JSX.Element {
  const {t} = useTranslation();
  const config = REGISTRY.FiberSocialForm;
  type FiberSocialFormData = InferFormValues<typeof config>;
  const params = useParams();

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
  } = useForm<FiberSocialFormData>({
    resolver: zodResolver(config.schema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      socialHandle: '',
      address: '',
      phoneNumber: '',
      socialDetails: '',
      attachments: [],
      formType: 'Social_Media',
      language: getSfdcLanguage(params?.locale),
    },
  });

  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} = useSubmit<FiberSocialFormData>(
    config.id,
  );
  const id = useId();

  const onSubmit: SubmitHandler<FiberSocialFormData> = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_general_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription>
              <Description>{t('forms.fiberSocial.description')}</Description>
            </FormDescription>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberSocial.firstName')}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberSocial.lastName')}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('forms.fiberSocial.email')}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="socialHandle"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberSocial.socialHandle')}
                    id={`socialHandle-${id}`}
                    required={isRequired(fields.socialHandle)}
                    errorText={tSafe(errors.socialHandle?.message, t)}
                    maxLength={maxLength(fields.socialHandle)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="address"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberSocial.address.label')}
                    id={`address-${id}`}
                    required={isRequired(fields.address)}
                    errorText={tSafe(errors.address?.message, t)}
                    maxLength={maxLength(fields.address)}
                    helpText={t('forms.fiberSocial.address.helpText')}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="phoneNumber"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.fiberSocial.phoneNumber')}
                    id={`phoneNumber-${id}`}
                    required={isRequired(fields.phoneNumber)}
                    errorText={tSafe(errors.phoneNumber?.message, t)}
                    maxLength={maxLength(fields.phoneNumber)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="socialDetails"
                render={({field}) => (
                  <TextArea
                    {...field}
                    label={t('forms.fiberSocial.socialDetails.label')}
                    id={`socialDetails-${id}`}
                    required={isRequired(fields.socialDetails)}
                    errorText={tSafe(errors.socialDetails?.message, t)}
                    maxLength={maxLength(fields.socialDetails)}
                    helpText={t('forms.fiberSocial.socialDetails.helpText')}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="attachments"
                control={control}
                render={({field}) => (
                  <>
                    <FileUpload
                      value={field.value ?? []}
                      instruction={t(
                        'forms.fiberSocial.attachments.instruction',
                      )}
                      buttonLabel={t('fileUpload.buttonLabel')}
                      defaultFileName={t('fileUpload.defaultFileName')}
                      fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                      maxFileSizeErrorMessage={t('errors.fileTooBig', {
                        size: constants.MAX_FILE_SIZE_MB,
                      })}
                      fileTypeErrorMessage={t('errors.invalidFileType')}
                      accept={constants.ACCEPTED_FILE_TYPES_STRING}
                      maxFiles={5}
                      onFileSelect={(_id, files) => field.onChange(files)}
                    />
                    <FormError
                      message={tSafe(errors.attachments?.message, t)}
                    />
                  </>
                )}
              />
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
            <Consent />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </>
  );
}

export {FiberSocialForm};
